import type { CommonEvent } from './schema';
import { getConfig } from './config';

/**
 * 디버그 로깅 유틸리티
 */

let debugEnabled = false;

export function setDebugMode(enabled: boolean): void {
    debugEnabled = enabled;

    // 디버그 모드 활성화 시 현재 설정 출력
    if (enabled) {
        const config = getConfig();
        console.group('🔧 Analytics Configuration');
        console.log('Environment:', process.env.NODE_ENV);
        console.log('Limits:', {
            impressionPerSession: config.limits.impressionPerSession === Infinity
                ? '∞ (Unlimited)'
                : config.limits.impressionPerSession,
            minorInteractionPerSession: config.limits.minorInteractionPerSession === Infinity
                ? '∞ (Unlimited)'
                : config.limits.minorInteractionPerSession,
        });
        console.log('Sampling:', config.sampling);
        console.log('Endpoint:', config.endpoint);
        console.groupEnd();
    }
}

export function isDebugMode(): boolean {
    return debugEnabled;
}

/**
 * 이벤트 전송 전 디버그 로그 출력
 */
export function debugLogEvent(event: CommonEvent): void {
    if (!debugEnabled) return;

    console.group(`📊 Analytics Event: ${event.event_name}`);

    // 기본 정보
    console.log('Event ID:', event.event_id);
    console.log('Timestamp:', new Date(event.client_ts).toISOString());
    console.log('Page:', event.page.path);

    if (event.user_id) {
        console.log('User ID:', event.user_id);
    }

    console.log('Session ID:', event.session_id);
    console.log('View ID:', event.view_id);  // 🆕 view_id 추가
    console.log('Anonymous ID:', event.anon_id);

    // Props가 있으면 테이블로 출력
    if (event.props && Object.keys(event.props).length > 0) {
        console.log('Props:');
        console.table(event.props);
    }

    // UTM 정보
    const utmKeys = Object.keys(event.utm).filter(key =>
        event.utm[key as keyof typeof event.utm] !== null &&
        event.utm[key as keyof typeof event.utm] !== undefined
    );
    if (utmKeys.length > 0) {
        console.log('UTM:', event.utm);
    }

    // A/B 테스트 정보
    if (event.exp?.ab && Object.keys(event.exp.ab).length > 0) {
        console.log('A/B Buckets:', event.exp.ab);
    }

    console.groupEnd();
}

/**
 * 배치 전송 디버그 로그
 */
export function debugLogBatch(events: CommonEvent[], endpoint: string): void {
    if (!debugEnabled) return;

    const payloadSize = JSON.stringify(events).length;
    const payloadSizeKB = (payloadSize / 1024).toFixed(2);

    console.group(`📤 Analytics Batch Send (${events.length} events, ${payloadSizeKB}KB)`);
    console.log('Endpoint:', endpoint);
    console.log('Events:', events.map(e => `${e.event_name} (${e.event_id.slice(-6)})`).join(', '));

    // 이벤트 타입별 카운트
    const eventCounts = events.reduce((acc, event) => {
        acc[event.event_name] = (acc[event.event_name] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    console.table(eventCounts);
    console.groupEnd();
}

/**
 * 전송 실패 디버그 로그
 */
export function debugLogError(error: Error, events: CommonEvent[], endpoint: string): void {
    if (!debugEnabled) return;

    console.group('❌ Analytics Send Failed');
    console.error('Error:', error.message);
    console.log('Endpoint:', endpoint);
    console.log('Events count:', events.length);
    console.log('Event IDs:', events.map(e => e.event_id.slice(-6)));
    console.groupEnd();
}

/**
 * 오프라인 큐 디버그 로그
 */
export function debugLogOfflineQueue(action: 'store' | 'restore' | 'clear', count: number): void {
    if (!debugEnabled) return;

    const actionEmoji = {
        store: '💾',
        restore: '🔄',
        clear: '🗑️'
    };

    console.log(`${actionEmoji[action]} Analytics Offline Queue: ${action} ${count} events`);
}

/**
 * 샘플링 디버그 로그
 */
export function debugLogSampling(eventName: string, sampled: boolean, rate: number): void {
    if (!debugEnabled) return;

    const status = sampled ? '✅' : '❌';
    console.log(`${status} Sampling ${eventName}: ${sampled ? 'included' : 'excluded'} (rate: ${rate})`);
}

/**
 * 세션 한도 디버그 로그
 */
export function debugLogLimit(type: string, allowed: boolean, current: number, limit: number): void {
    if (!debugEnabled) return;

    const status = allowed ? '✅' : '🚫';
    console.log(`${status} Limit check ${type}: ${current}/${limit} ${allowed ? 'allowed' : 'exceeded'}`);
}

/**
 * 스키마 검증 실패 디버그 로그
 */
export function debugLogSchemaError(eventName: string, error: any): void {
    if (!debugEnabled) return;

    console.group('⚠️ Schema Validation Failed');
    console.error('Event:', eventName);
    console.error('Validation Error:', error);

    // Zod 에러인 경우 더 자세한 정보 출력
    if (error && error.issues) {
        console.error('Zod Issues:', error.issues);
        error.issues.forEach((issue: any, index: number) => {
            console.error(`Issue ${index + 1}:`, {
                path: issue.path,
                code: issue.code,
                message: issue.message,
                received: issue.received,
                expected: issue.expected
            });
        });
    }

    console.groupEnd();
}

/**
 * 개발자 도구용 전역 디버그 함수
 */
declare global {
    interface Window {
        __analytics?: {
            flush: () => Promise<void>;
            getConfig: () => any;
            getSessionInfo: () => any;
            clearOfflineQueue: () => void;
            debugMode: (enabled: boolean) => void;
        };
    }
}

export function setupGlobalDebug(
    flushFn: () => Promise<void>,
    getSessionInfoFn: () => any,
    clearOfflineQueueFn: () => void
): void {
    if (typeof window === 'undefined') return;

    window.__analytics = {
        flush: flushFn,
        getConfig: getConfig,
        getSessionInfo: getSessionInfoFn,
        clearOfflineQueue: clearOfflineQueueFn,
        debugMode: setDebugMode,
    };

    if (debugEnabled) {
        console.log('🔧 Analytics debug tools available at window.__analytics');
    }
}
